// src/components/WOEPayment/hooks/usePaymentCalculations.js
import { useState, useCallback, useEffect, useMemo } from 'react';
import { isIsraelUser, requiresTaxCalculation } from '../../../../utils/locationUtils';
import { normalizeOrderType } from '../../../../utils/orderTypeUtils';

const usePaymentCalculations = (orderItems, accessories, customerInfo, savedPaymentData) => {
    
    // Payment state
    const [payments, setPayments] = useState(
        savedPaymentData?.payments || [
            {
                id: 1,
                method: 'Credit Card',
                amount: 0,
                notes: '',
                cardFee: 0
            }
        ]
    );

    // Totals state
    const [totals, setTotals] = useState({
        subtotal: 0,
        tax: 0,
        taxRate: 0,
        total: 0,
        cardFees: 0
    });

    // Payment summary state
    // 🔧 FIX: Never restore isComplete or invoiceNumber from saved data
    // These should only be set after a successful submission, not from persisted state
    const [paymentSummary, setPaymentSummary] = useState(() => {
        const saved = savedPaymentData?.paymentSummary;
        return {
            totalPaid: saved?.totalPaid || 0,
            balanceDue: saved?.balanceDue || 0,
            changeDue: saved?.changeDue || 0,
            paymentStatus: saved?.paymentStatus || 'PENDING',
            totalCardFees: saved?.totalCardFees || 0,
            // Always reset these - they should only be set after submission
            isComplete: false,
            invoiceNumber: ''
        };
    });

    // Tax location state
    const [userLocation, setUserLocation] = useState(null);
    const [taxLocationSelected, setTaxLocationSelected] = useState(false);

    // Calculate subtotal from items
    const calculateSubtotal = useMemo(() => {
        const itemsTotal = orderItems.reduce((sum, item) => 
            sum + ((parseFloat(item.price) || 0) * (parseInt(item.quantity) || 1)), 0);
        
        const accessoriesTotal = accessories.reduce((sum, acc) => 
            sum + ((parseFloat(acc.price) || 0) * (parseInt(acc.quantity) || 1)), 0);
        
        return itemsTotal + accessoriesTotal;
    }, [orderItems, accessories]);

    // Calculate credit card fee
    const calculateCardFee = useCallback((amount, method) => {
        // Israel users don't pay credit card fees
        if (isIsraelUser()) {
            return 0;
        }
        
        // For non-Israel users, apply 3% fee for payments over $150
        if (method === 'Credit Card' && parseFloat(amount) >= 150) {       
            return (parseFloat(amount) * 0.03);
        }
        return 0;
    }, []);

    // Add these functions after the existing calculateCardFee function
    const calculateTaxOnPayment = useCallback((paymentAmount, taxRate) => {
        if (isIsraelUser() || !requiresTaxCalculation(customerInfo?.orderType)) {
            return 0;
        }
        return parseFloat(paymentAmount) * (parseFloat(taxRate) / 100);
    }, [customerInfo?.orderType]);


    const calculateTotalChargeWithTax = useCallback((paymentAmount, taxRate, cardFee = 0) => {
        const taxOnPayment = calculateTaxOnPayment(paymentAmount, taxRate);
        return parseFloat(paymentAmount) + taxOnPayment + parseFloat(cardFee);
    }, [calculateTaxOnPayment]);

    // Determine tax rate based on location and order type
    const determineTaxRate = useCallback(() => {
        // Israel users never pay tax
        if (isIsraelUser()) {
            return { taxRate: 0, state: 'ISRAEL', source: 'israel_user' };
        }

        // Only apply tax if order type requires it for non-Israel users
        if (!requiresTaxCalculation(customerInfo?.orderType)) {
            return { taxRate: 0, state: null, source: 'non_taxable_order' };
        }

        // 🔧 FIX: Check userLocation state FIRST (user selection takes priority)
        if (userLocation) {
            const location = userLocation.toUpperCase().trim();

            switch (location) {
                case 'NY':
                case 'NEW YORK':
                case 'POMONA':
                    return { taxRate: 8.375, state: 'NY', source: 'user_selected_ny' };
                case 'NJ':
                case 'NEW JERSEY':
                case 'LAKEWOOD':
                    return { taxRate: 6.625, state: 'NJ', source: 'user_selected_nj' };
                default:
                    return { taxRate: 0, state: location, source: 'user_selected_other' };
            }
        }

        // For non-Israel users, fall back to localStorage user location for tax
        const userData = JSON.parse(localStorage.getItem('user') || '{}');

        if (userData.UserLocation) {
            const location = userData.UserLocation.toUpperCase().trim();

            switch (location) {
                case 'NY':
                case 'NEW YORK':
                case 'POMONA':
                    return { taxRate: 8.375, state: 'NY', source: 'user_location_ny' };
                case 'NJ':
                case 'NEW JERSEY':
                case 'LAKEWOOD':
                    return { taxRate: 6.625, state: 'NJ', source: 'user_location_nj' };
                default:
                    return { taxRate: 0, state: null, source: 'unknown_location' };
            }
        }

        return { taxRate: 0, state: null, source: 'no_user_location' };
    }, [customerInfo?.orderType, userLocation]); // 🔧 Added userLocation to dependencies

    // Payment method handlers
    const handlePaymentMethodChange = useCallback((id, method) => {
        setPayments(payments => payments.map(payment => {
            if (payment.id === id) {
                const baseAmount = parseFloat(payment.amount) || 0;
                const taxOnPayment = calculateTaxOnPayment(baseAmount, totals.taxRate);
                const amountForCardFee = baseAmount + taxOnPayment;
                
                let cardFee = 0;
                if (method === 'Credit Card' && amountForCardFee >= 150 && !isIsraelUser()) {
                    cardFee = amountForCardFee * 0.03;
                }
                
                const totalCharge = baseAmount + taxOnPayment + cardFee;
                
                return { 
                    ...payment, 
                    method, 
                    taxOnPayment: taxOnPayment.toFixed(2),
                    cardFee: cardFee.toFixed(2),
                    totalCharge: totalCharge.toFixed(2)
                };
            }
            return payment;
        }));
    }, [calculateTaxOnPayment, totals.taxRate]);

    
    

    const handlePaymentAmountChange = useCallback((id, inputAmount) => {
        setPayments(payments => payments.map(payment => {
            if (payment.id === id) {
                const baseAmountTowardSubtotal = parseFloat(inputAmount) || 0;
                
                console.log('💰 Processing payment toward subtotal:', baseAmountTowardSubtotal);
                
                let taxOnPayment = 0;
                let cardFee = 0;
                let totalCharge = baseAmountTowardSubtotal;
                
                if (baseAmountTowardSubtotal > 0) {
                    // Calculate tax precisely and round to 2 decimal places
                    if (!isIsraelUser() && requiresTaxCalculation(customerInfo?.orderType) && totals.taxRate > 0) {
                        const taxPrecise = baseAmountTowardSubtotal * (totals.taxRate / 100);
                        taxOnPayment = parseFloat(taxPrecise.toFixed(2));
                        
                        console.log('🔍 Tax calculation:', {
                            baseAmount: baseAmountTowardSubtotal,
                            taxRate: totals.taxRate,
                            taxPrecise: taxPrecise,
                            taxRounded: taxOnPayment
                        });
                    }
                    
                    // Calculate card fee on total customer payment (base + tax), then round
                    const totalCustomerPayment = baseAmountTowardSubtotal + taxOnPayment;
                    if (payment.method === 'Credit Card' && totalCustomerPayment >= 150 && !isIsraelUser()) {
                        const cardFeePrecise = totalCustomerPayment * 0.03;
                        cardFee = parseFloat(cardFeePrecise.toFixed(2));
                        
                        console.log('💳 Card fee calculation:', {
                            totalCustomerPayment: totalCustomerPayment,
                            cardFeePrecise: cardFeePrecise,
                            cardFeeRounded: cardFee
                        });
                    }
                    
                    // 🔧 FIX: Ensure total charge is also rounded to prevent floating point issues
                    totalCharge = parseFloat((baseAmountTowardSubtotal + taxOnPayment + cardFee).toFixed(2));
                }
                
                console.log('💰 Final payment calculation:', {
                    inputAmount: inputAmount,
                    baseAmountTowardSubtotal: baseAmountTowardSubtotal,
                    taxOnPayment: taxOnPayment,
                    cardFee: cardFee,
                    totalCharge: totalCharge,
                    verification: `${baseAmountTowardSubtotal} + ${taxOnPayment} + ${cardFee} = ${totalCharge}`,
                    note: 'Rounded values - backend will receive exactly these amounts'
                });
                
                return {
                    ...payment,
                    amount: inputAmount, // Store original input
                    baseAmountTowardSubtotal: baseAmountTowardSubtotal,
                    
                    // Store precisely rounded values for consistent display and backend
                    taxOnPayment: taxOnPayment,
                    cardFee: cardFee,
                    totalCharge: totalCharge,
                    
                    // Backward compatibility
                    baseAmount: baseAmountTowardSubtotal
                };
            }
            return payment;
        }));
    }, [customerInfo?.orderType, totals.taxRate]);

    const handlePaymentNotesChange = useCallback((id, notes) => {
        setPayments(payments => payments.map(payment => 
            payment.id === id ? { ...payment, notes } : payment
        ));
    }, []);
    
    const addPayment = useCallback(() => {
        const newId = Math.max(...payments.map(p => p.id), 0) + 1;
        
        // ✅ FIX: Calculate remaining SUBTOTAL balance, not total balance
        const orderSubtotal = parseFloat(calculateSubtotal);
        const totalPaidTowardSubtotal = payments.reduce((sum, payment) => {
            return sum + (parseFloat(payment.amount) || 0); // This is amount toward subtotal
        }, 0);
        
        const remainingSubtotalBalance = Math.max(0, orderSubtotal - totalPaidTowardSubtotal);
        
        console.log('💰 Adding new payment method:', {
            orderSubtotal,
            totalPaidTowardSubtotal,
            remainingSubtotalBalance,
            calculation: `${orderSubtotal} - ${totalPaidTowardSubtotal} = ${remainingSubtotalBalance}`,
            note: 'New payment will be toward remaining SUBTOTAL (tax calculated on top)'
        });
        
        const newPaymentMethod = 'Cash'; // Default to cash to avoid fees
        
        // Create new payment with remaining subtotal amount
        const newPayment = {
            id: newId,
            method: newPaymentMethod,
            amount: remainingSubtotalBalance.toFixed(2), // Amount toward subtotal
            notes: '',
            
            // Initialize calculation fields (will be calculated by handlePaymentAmountChange)
            taxOnPayment: 0,
            cardFee: 0,
            totalCharge: remainingSubtotalBalance.toFixed(2),
            baseAmountTowardSubtotal: remainingSubtotalBalance
        };
        
        console.log('💳 New payment method created:', {
            id: newId,
            method: newPaymentMethod,
            amountTowardSubtotal: remainingSubtotalBalance.toFixed(2),
            willCalculateTax: !isIsraelUser() && requiresTaxCalculation(customerInfo?.orderType),
            expectedTax: !isIsraelUser() && requiresTaxCalculation(customerInfo?.orderType) ? 
                (remainingSubtotalBalance * (totals.taxRate / 100)).toFixed(2) : '0.00'
        });
        
        setPayments(prev => [...prev, newPayment]);
        
        // Trigger recalculation for the new payment
        // This will calculate tax and fees properly
        setTimeout(() => {
            handlePaymentAmountChange(newId, remainingSubtotalBalance.toFixed(2));
        }, 0);
        
    }, [payments, calculateSubtotal, customerInfo?.orderType, totals.taxRate, handlePaymentAmountChange]);

    const removePayment = useCallback((id) => {
        if (payments.length <= 1) return;
        setPayments(payments => payments.filter(payment => payment.id !== id));
    }, [payments.length]);

    // Tax location management
    const updateTotalsWithTaxRate = useCallback((newTaxRate, taxState = null) => {
        const subtotal = calculateSubtotal;
        const tax = subtotal * (newTaxRate / 100);
        const total = subtotal + tax;
        
        const cardFees = payments.reduce((total, payment) => {
            return total + calculateCardFee(payment.amount, payment.method);
        }, 0);
        
        setTotals({
            subtotal: subtotal,
            tax: tax,
            taxRate: newTaxRate,
            taxState: taxState,
            total: total,
            cardFees: cardFees
        });
    }, [calculateSubtotal, payments, calculateCardFee]);

    // Find this switch statement in usePaymentCalculations.js and add the default case:

const handleLocationSelect = useCallback((selectedLocation) => {
    console.log('🏠 Tax location selected:', selectedLocation);
    
    setTaxLocationSelected(true);
    setUserLocation(selectedLocation);
    
    let taxRate = 0;
    const location = selectedLocation.toUpperCase();
    
    switch (location) {
        case 'NY':
        case 'NEW YORK':
            taxRate = 8.375;
            break;
        case 'NJ':
        case 'NEW JERSEY':
            taxRate = 6.625;
            break;
        case 'ISRAEL':
        case 'OTHER':
            taxRate = 0;
            break;
        default:
            taxRate = 0;
            break;
    }
    
    console.log('🏠 Tax location changed:', {
        selectedLocation,
        newTaxRate: taxRate,
        existingPayments: payments.length,
        willRecalculateBreakdowns: true
    });
    
    // Update the global tax rate first
    updateTotalsWithTaxRate(taxRate, location);
    
    // ENHANCED: Force immediate recalculation of ALL payment breakdowns
    // Use multiple strategies to ensure the UI updates immediately
    
    // Strategy 1: Immediate recalculation (synchronous)
    setPayments(currentPayments => {
        return currentPayments.map(payment => {
            if (payment.amount && parseFloat(payment.amount) > 0) {
                const baseAmount = parseFloat(payment.amount);
                
                // Recalculate tax with new rate
                let taxOnPayment = 0;
                if (!isIsraelUser() && requiresTaxCalculation(customerInfo?.orderType) && taxRate > 0) {
                    taxOnPayment = parseFloat((baseAmount * (taxRate / 100)).toFixed(2));
                }
                
                // Recalculate card fee
                let cardFee = 0;
                const totalCustomerPayment = baseAmount + taxOnPayment;
                if (payment.method === 'Credit Card' && totalCustomerPayment >= 150 && !isIsraelUser()) {
                    cardFee = parseFloat((totalCustomerPayment * 0.03).toFixed(2));
                }
                
                const totalCharge = parseFloat((baseAmount + taxOnPayment + cardFee).toFixed(2));
                
                console.log(`💰 Immediate recalc for payment ${payment.id}:`, {
                    oldTax: payment.taxOnPayment,
                    newTax: taxOnPayment,
                    oldFee: payment.cardFee,
                    newFee: cardFee,
                    oldTotal: payment.totalCharge,
                    newTotal: totalCharge
                });
                
                return {
                    ...payment,
                    taxOnPayment: taxOnPayment,
                    cardFee: cardFee,
                    totalCharge: totalCharge
                };
            }
            return payment;
        });
    });
    
}, [updateTotalsWithTaxRate, customerInfo?.orderType]);

    // Payment requirement checking
    const checkPaymentRequirements = useCallback((paymentsArray, totalsData) => {
        // Calculate total paid TOWARD SUBTOTAL (not including tax/fees)
        const totalPaidTowardSubtotal = paymentsArray.reduce((sum, payment) => {
            return sum + (parseFloat(payment.amount) || 0); // This is amount toward subtotal
        }, 0);
        
   
        
        // "Aidel New Wig" / "Aidel Wig Repair" follow the same rules as their base types.
        const baseOrderType = normalizeOrderType(customerInfo?.orderType);

        // New Wig: Minimum $2,500 toward subtotal
        if (baseOrderType === 'New Wig') {
            const meetsRequirement = totalPaidTowardSubtotal >= 2500;
            return meetsRequirement;
        }

        // Wig Repair over $1,000: 50% of subtotal required
        if (baseOrderType === 'Wig Repair' && parseFloat(totalsData.subtotal) > 1000) {
            const requiredAmount = parseFloat(totalsData.subtotal) * 0.5;
            const meetsRequirement = totalPaidTowardSubtotal >= requiredAmount;
            console.log('🔧 Wig Repair requirement:', {
                orderSubtotal: totalsData.subtotal,
                required: requiredAmount,
                paid: totalPaidTowardSubtotal,
                meets: meetsRequirement
            });
            return meetsRequirement;
        }
        
        // New Sale Wig or Consultation: Must pay full subtotal
        if (baseOrderType === 'New Sale Wig' || baseOrderType === 'Consultation') {
            const requiredAmount = parseFloat(totalsData.subtotal);
            const meetsRequirement = totalPaidTowardSubtotal >= requiredAmount;
            console.log('💰 Full payment requirement:', {
                orderType: customerInfo?.orderType,
                orderSubtotal: totalsData.subtotal,
                required: requiredAmount,
                paid: totalPaidTowardSubtotal,
                meets: meetsRequirement
            });
            return meetsRequirement;
        }
        
        // Other order types have no specific payment requirements
        console.log('✅ No specific payment requirements for order type:', customerInfo?.orderType);
        return true;
    }, [customerInfo?.orderType]);
    // ADD this function to your usePaymentCalculations.js
    const handlePayInFull = useCallback(() => {
        console.log('💰 Pay in Full button clicked');
        
        // Calculate remaining subtotal balance
        const orderSubtotal = parseFloat(calculateSubtotal);
        const currentSubtotalPaid = payments.reduce((sum, payment) => {
            return sum + (parseFloat(payment.amount) || 0); // Input amounts toward subtotal
        }, 0);
        
        const remainingSubtotal = Math.max(0, orderSubtotal - currentSubtotalPaid);
        
        console.log('💰 Pay in Full calculation:', {
            orderSubtotal,              // e.g., 5700
            currentSubtotalPaid,        // e.g., 3000
            remainingSubtotal,          // e.g., 2700
            willCalculateTax: `${remainingSubtotal} × ${totals.taxRate}% = ${(remainingSubtotal * (totals.taxRate / 100)).toFixed(2)}`
        });
        
        if (remainingSubtotal <= 0.01) {
            console.log('💰 No remaining subtotal to pay');
            return;
        }
        
        // Set the payment amount to remaining subtotal (tax will be calculated automatically)
        if (payments.length > 0) {
            handlePaymentAmountChange(payments[0].id, remainingSubtotal.toFixed(2));
            
            // Remove extra payments
            const extraPayments = payments.slice(1);
            extraPayments.forEach(payment => {
                removePayment(payment.id);
            });
            
            console.log('✅ Pay in Full completed - set payment to remaining subtotal:', remainingSubtotal.toFixed(2));
        }
    }, [payments, calculateSubtotal, totals.taxRate, handlePaymentAmountChange, removePayment]);
    

    // Tax requirement checking
    const checkTaxRequirements = useCallback(() => {
        // Israel users never need tax selection
        if (isIsraelUser()) {
            return true;
        }
        
        // For non-Israel users with tax-eligible orders, require tax location selection
        if (requiresTaxCalculation(customerInfo?.orderType)) {
            return taxLocationSelected || userLocation;
        }
        
        return true;
    }, [customerInfo?.orderType, taxLocationSelected, userLocation]);

    // Initialize totals when component mounts or items change
    useEffect(() => {
        const subtotal = calculateSubtotal;
        let taxRate = 0;
        let tax = 0;
        let taxState = null;

        // Israel users always get 0% tax
        if (isIsraelUser()) {
            taxRate = 0;
            tax = 0;
            taxState = 'ISRAEL';
        } else {
            // For non-Israel users, use existing tax determination logic
            const taxInfo = determineTaxRate();
            taxRate = taxInfo.taxRate;
            taxState = taxInfo.state; // 🔧 FIX: Capture the state from determineTaxRate

            if (requiresTaxCalculation(customerInfo?.orderType)) {
                tax = subtotal * (taxRate / 100);
            }

             // FIX: If tax rate was automatically determined, mark as selected
            if (taxRate > 0) {
                setTaxLocationSelected(true);
            }
        }

        const total = subtotal + tax;

        setTotals({
            subtotal: subtotal,
            tax: tax,
            taxRate: taxRate,
            taxState: taxState, // 🔧 FIX: Set the tax state in totals
            total: total,
            cardFees: 0
        });
        
        // Initialize payments if not already set
        if (!savedPaymentData?.payments) {
            // Israel users don't get credit card fees
            const cardFee = isIsraelUser() ? 0 : calculateCardFee(total, 'Credit Card');
            
            const initialPayments = [{
                id: 1,
                method: 'Credit Card',
                amount: total.toFixed(2),
                notes: '',
                cardFee: cardFee,
                totalCharge: (total + cardFee).toFixed(2)
            }];
            
            setPayments(initialPayments);
        }
    }, [calculateSubtotal, determineTaxRate, customerInfo?.orderType, calculateCardFee, savedPaymentData?.payments]);



    useEffect(() => {
        // Calculate totals using the same rounded values stored in payments
        const totalCardFeesRounded = payments.reduce((sum, payment) => {
            return sum + (parseFloat(payment.cardFee) || 0); // These are already rounded
        }, 0);
        
        const totalPaidTowardSubtotal = payments.reduce((sum, payment) => {
            return sum + (parseFloat(payment.amount) || 0);
        }, 0);
        
        const totalChargedToCustomer = payments.reduce((sum, payment) => {
            return sum + (parseFloat(payment.totalCharge) || parseFloat(payment.amount) || 0);
        }, 0);
        
        // Calculate balance using rounded values
        const orderSubtotal = parseFloat(totals.subtotal);
        const subtotalDifference = totalPaidTowardSubtotal - orderSubtotal;
    
        console.log('📊 Payment Summary (CONSISTENT ROUNDING):', {
            orderSubtotal: orderSubtotal,
            totalPaidTowardSubtotal: totalPaidTowardSubtotal,
            totalChargedToCustomer: totalChargedToCustomer,
            totalCardFees: totalCardFeesRounded,
            subtotalDifference: subtotalDifference,
            note: 'All values consistently rounded - backend will get exactly these amounts'
        });
    
        let newPaymentSummary;
        
        // Use 1 cent tolerance for balance checking
        if (Math.abs(subtotalDifference) < 0.01) {
            newPaymentSummary = {
                totalPaid: totalPaidTowardSubtotal.toFixed(2),
                totalCharged: totalChargedToCustomer.toFixed(2),
                balanceDue: "0.00",
                changeDue: "0.00", 
                paymentStatus: "PAID IN FULL",
                totalCardFees: totalCardFeesRounded.toFixed(2)
            };
        } else if (subtotalDifference > 0.01) {
            newPaymentSummary = {
                totalPaid: totalPaidTowardSubtotal.toFixed(2),
                totalCharged: totalChargedToCustomer.toFixed(2),
                balanceDue: "0.00",
                changeDue: subtotalDifference.toFixed(2),
                paymentStatus: "CHANGE DUE",
                totalCardFees: totalCardFeesRounded.toFixed(2)
            };
        } else {
            newPaymentSummary = {
                totalPaid: totalPaidTowardSubtotal.toFixed(2),
                totalCharged: totalChargedToCustomer.toFixed(2),
                balanceDue: Math.abs(subtotalDifference).toFixed(2),
                changeDue: "0.00",
                paymentStatus: "BALANCE DUE", 
                totalCardFees: totalCardFeesRounded.toFixed(2)
            };
        }
        
        setPaymentSummary(prev => ({
            ...prev,
            ...newPaymentSummary
        }));
        
        setTotals(prev => ({
            ...prev,
            cardFees: totalCardFeesRounded
        }));
    }, [payments, totals.subtotal, totals.tax, totals.total]);
    // Computed values
    const paymentRequirementMet = useMemo(() => {
        return checkPaymentRequirements(payments, totals); // Pass totals object instead of totals.total
    }, [checkPaymentRequirements, payments, totals]);

    const taxRequirementMet = useMemo(() => {
        return checkTaxRequirements();
    }, [checkTaxRequirements]);

    const getPaymentRequirementText = useCallback(() => {
        const totalPaidTowardSubtotal = payments.reduce((sum, payment) => {
            return sum + (parseFloat(payment.amount) || 0);
        }, 0);
        
        // "Aidel New Wig" / "Aidel Wig Repair" follow the same rules as their base types.
        const baseOrderType = normalizeOrderType(customerInfo?.orderType);

        if (baseOrderType === 'New Wig') {
            const remaining = Math.max(0, 2500 - totalPaidTowardSubtotal);
            if (remaining > 0) {
                return `New Wig orders require a minimum payment of $2,500 toward subtotal. Need $${remaining.toFixed(2)} more.`;
            }
            return "✅ New Wig minimum payment requirement met ($2,500 toward subtotal).";
        } else if (baseOrderType === 'Wig Repair' && parseFloat(totals.subtotal) > 1000) {
            const required = parseFloat(totals.subtotal) * 0.5;
            const remaining = Math.max(0, required - totalPaidTowardSubtotal);
            if (remaining > 0) {
                return `Wig Repair orders over $1,000 require 50% of subtotal (${required.toFixed(2)}). Need $${remaining.toFixed(2)} more.`;
            }
            return "✅ Wig Repair minimum payment requirement met (50% of subtotal).";
        } else if (baseOrderType === 'New Sale Wig' || baseOrderType === 'Consultation') {
            const required = parseFloat(totals.subtotal);
            const remaining = Math.max(0, required - totalPaidTowardSubtotal);
            if (remaining > 0) {
                return `${customerInfo.orderType} orders require full subtotal payment (${required.toFixed(2)}). Need $${remaining.toFixed(2)} more.`;
            }
            return `✅ ${customerInfo.orderType} full subtotal payment requirement met.`;
        }
        
        return null;
    }, [customerInfo?.orderType, totals.subtotal, payments]);

    const getTaxRequirementText = useCallback(() => {
        // No tax requirements for Israel users
        if (isIsraelUser()) {
            return null;
        }
        
        if (requiresTaxCalculation(customerInfo?.orderType) && !taxRequirementMet) {
            return "Note: Tax-eligible orders require tax location selection before checkout.";
        }
        return null;
    }, [customerInfo?.orderType, taxRequirementMet]);

    return {
        // State
        payments,
        totals,
        paymentSummary,
        userLocation,
        taxLocationSelected,
        
        // Actions
        setPayments,
        setPaymentSummary,
        handlePaymentMethodChange,
        handlePaymentAmountChange,
        handlePaymentNotesChange,
        handlePayInFull,
        handleLocationSelect,
        addPayment,
        removePayment,
        updateTotalsWithTaxRate,
        setUserLocation,
        setTaxLocationSelected,
        
        // Computed values
        calculateSubtotal,
        paymentRequirementMet,
        taxRequirementMet,
        calculateTaxOnPayment,
        
        // Helper functions
        calculateCardFee,
        checkPaymentRequirements,
        checkTaxRequirements,
        getPaymentRequirementText,
        getTaxRequirementText,
        
        // Utilities
        formatCurrency: (amount) => parseFloat(amount).toFixed(2)
    };
};

export default usePaymentCalculations;